import { InteractionMap } from "../view/interactions";
import { ModuleID } from "../types/ids";
import {
  ErrorModuleRef,
  errorModuleRefBrand,
  LoadingModuleRef,
  ModuleRef,
  ModulesConfig,
  ViewModuleRef,
} from "../view/types";
import { VersionString } from "../manifest/types";
import type { PathParams } from "../types/ids";
import type { ViewModuleRefDataParams } from "../view/types";
import {
  ErrorModuleIdArg,
  ErrorModuleVersionArg,
  LoadingModuleIdArg,
  LoadingModuleVersionArg,
  RegisteredModuleDataParams,
  RegisteredModuleProps,
  ViewModuleIdArg,
  ViewModuleVersionArg,
} from "./registry";

// ============================================================================
// module() helper
// ============================================================================

type RequiredKeys<T> = {
  [K in keyof T]-?: Record<never, never> extends Pick<T, K> ? never : K;
}[keyof T];

type DataParamsSource<TDataParams, TPathParams> =
  | TDataParams
  | ((pathParams: TPathParams) => TDataParams);

type ExcessKeys<TActual, TDataParams> = Exclude<keyof TActual, keyof TDataParams>;

type ForbidExcessKeys<TActual, TDataParams> = [
  ExcessKeys<TActual, TDataParams>,
] extends [never]
  ? unknown
  : { [K in ExcessKeys<TActual, TDataParams>]: never };

// TypeScript skips excess-property checks on callback return values, so
// unknown keys are rejected explicitly for both forms.
type ExactDataParamsSource<TSource, TDataParams> = TSource extends (
  ...args: Array<never>
) => infer TReturn
  ? TSource & ForbidExcessKeys<TReturn, TDataParams>
  : TSource & ForbidExcessKeys<TSource, TDataParams>;

// Options are required exactly when the module declares a required param.
type ModuleRefOptionsArgs<TDataParams, TSource> = [
  RequiredKeys<TDataParams>,
] extends [never]
  ? [options?: { dataParams?: ExactDataParamsSource<TSource, TDataParams> }]
  : [options: { dataParams: ExactDataParamsSource<TSource, TDataParams> }];

/**
 * Type-safe module reference helper, bound to a view's data/state/interaction
 * types and generic per call over the referenced module's id and pinned
 * version. Props are checked against the {@link ModuleRegistry} contract for
 * exactly that id + version.
 *
 * This is the type of the `mod` argument passed to a view's `modules` and
 * `layoutOverride` callbacks. The standalone {@link moduleRef} export is an
 * instance with the view-context types left at their `unknown` defaults.
 * For flow-level `layout`, use {@link flowModuleRef}; for `errorView`, use
 * {@link errorModuleRef}.
 *
 * Until a module registers, `ModuleRegistry` is empty and `keyof ModuleRegistry`
 * is `never`, so `module()` rejects every id — there is no valid module id until
 * one exists. Views load the populated registry via tsconfig `types` and see the
 * real id / version / prop contracts.
 *
 * @example
 * ```ts
 * // Static props — plain object, no view context needed:
 * modules: mod => [
 *   mod("sample/sample-form", "1.1.0", { title: "My Form" }),
 * ],
 *
 * // Callback props — receive a fully-typed {@link ModulesConfig}: `interactions`
 * // are wired handlers, plus server `data`, `viewState`, and `setViewState`:
 * modules: mod => [
 *   mod("sample/sample-form", "1.1.0", ({ interactions, viewState, data }) => ({
 *     title: viewState.formTitle,
 *     onSubmit: interactions.submitForm,
 *     defaultEmail: data.user.email,
 *   })),
 * ],
 *
 * // layoutOverride returns a single ModuleRef:
 * layoutOverride: mod =>
 *   mod("shared/main-layout", "1.0.0", ({ data }) => ({ showHeader: data.isLoggedIn })),
 *
 * // Data params — static, or mapped from the view's own route params only:
 * // (route "/:appId/personal")
 * modules: mod => [
 *   mod("enrollment/personal-info", "2.0.0", {}, {
 *     dataParams: (pathParams) => ({ applicationId: pathParams.appId }),
 *   }),
 * ],
 * ```
 */
export type ModuleHelper<
  TServerData = unknown,
  TViewState = unknown,
  TInteractions extends InteractionMap = InteractionMap,
  TPathParams = PathParams,
> = <
  TId extends ViewModuleIdArg,
  TVersion extends ViewModuleVersionArg<TId>,
  TDataParamsSource extends DataParamsSource<
    RegisteredModuleDataParams<TId, TVersion>,
    TPathParams
  > = DataParamsSource<RegisteredModuleDataParams<TId, TVersion>, TPathParams>,
>(
  moduleId: TId,
  minVersion: TVersion,
  // Props may be a plain record or a callback receiving a fully-typed
  // ModulesConfig; either way it must satisfy the registry contract for the
  // referenced id + pinned version.
  props:
    | RegisteredModuleProps<TId, TVersion>
    | ((
        config: ModulesConfig<TServerData, TViewState, TInteractions>,
      ) => RegisteredModuleProps<TId, TVersion>),
  // `dataParams` is either static or derived from the view's route params
  // alone — never from view data, state, or interactions.
  ...options: ModuleRefOptionsArgs<
    RegisteredModuleDataParams<TId, TVersion>,
    TDataParamsSource
  >
) => ViewModuleRef<TServerData, TViewState, TInteractions>;

/**
 * Create a `ModuleRef` — a reference to a module with props and optional
 * data params. Typed by {@link ModuleHelper}; the runtime just packages the
 * arguments into a `ModuleRef`.
 *
 * Use inside `defineView` via the pre-bound `mod` argument so the view's context
 * types flow into prop callbacks. For flow-level refs use {@link flowModuleRef}
 * or {@link errorModuleRef}.
 *
 * Typed `ModuleHelper<any, any, any, any>` so a view of *any* context type can accept
 * it as its `modules` / `layoutOverride` argument — the view binds its own
 * context through the parameter's `ModuleHelper<TServerData, …>`, so the `any`
 * here never weakens in-view prop typing.
 */
function buildModuleRef<TDataParams>(
  moduleId: ModuleID,
  minVersion: VersionString,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- runtime body only packages already-validated args; strict contracts are enforced by callers
  props: any,
  options?: { dataParams?: TDataParams },
) {
  const dataParams = options?.dataParams;
  return {
    _tag: "ModuleRef" as const,
    moduleId,
    minVersion,
    props,
    ...(dataParams === undefined ? {} : { dataParams }),
  };
}

function createModuleRef(
  moduleId: ModuleID,
  minVersion: VersionString,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- runtime body only packages already-validated args; the strict contract (id → version → props) is enforced by the ModuleHelper type below
  props: any,
  options?: { dataParams?: ViewModuleRefDataParams },
): ViewModuleRef<unknown, unknown, InteractionMap> {
  return buildModuleRef(moduleId, minVersion, props, options);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- context generics are `any` so `moduleRef` is assignable to any view's ModuleHelper<S, V, I, P>; per-call id/version/props checking is unaffected
export const moduleRef: ModuleHelper<any, any, any, any> = createModuleRef;

export type ErrorModuleHelper = <
  TId extends ErrorModuleIdArg,
  TVersion extends ErrorModuleVersionArg<TId>,
  TDataParams extends RegisteredModuleDataParams<
    TId,
    TVersion
  > = RegisteredModuleDataParams<TId, TVersion>,
>(
  moduleId: TId,
  minVersion: TVersion,
  props: RegisteredModuleProps<TId, TVersion>,
  ...options: ModuleRefOptionsArgs<
    RegisteredModuleDataParams<TId, TVersion>,
    TDataParams
  >
) => ErrorModuleRef;

function createErrorModuleRef(
  moduleId: ModuleID,
  minVersion: VersionString,
  // `unknown` — implementation must accept every `RegisteredModuleProps` variant
  // (including inferred `P` shapes that are not assignable to Record<string, unknown>
  // under strictFunctionTypes). Call-site checking stays on ErrorModuleHelper.
  props: unknown,
  options?: { dataParams?: Record<string, unknown> },
): ErrorModuleRef {
  return {
    ...buildModuleRef(moduleId, minVersion, props, options),
    [errorModuleRefBrand]: true,
  };
}

export const errorModuleRef: ErrorModuleHelper = createErrorModuleRef;

// ============================================================================
// flowModuleRef() helper
// ============================================================================

/**
 * Type-safe module reference helper for flow-level `layout`.
 *
 * Like {@link moduleRef}, props are checked against the {@link ModuleRegistry}
 * contract for the referenced id + version. Unlike `moduleRef`:
 *
 * - Only **static** props and data params are accepted (no view context).
 * - Returns a plain {@link ModuleRef} (not {@link ViewModuleRef}), matching
 *   `Flow.layout`.
 *
 * For `errorView`, use {@link errorModuleRef} instead.
 */
export type FlowModuleHelper = <
  TId extends ViewModuleIdArg,
  TVersion extends ViewModuleVersionArg<TId>,
  TDataParams extends RegisteredModuleDataParams<
    TId,
    TVersion
  > = RegisteredModuleDataParams<TId, TVersion>,
>(
  moduleId: TId,
  minVersion: TVersion,
  props: RegisteredModuleProps<TId, TVersion>,
  ...options: ModuleRefOptionsArgs<
    RegisteredModuleDataParams<TId, TVersion>,
    TDataParams
  >
) => ModuleRef;

function createFlowModuleRef(
  moduleId: ModuleID,
  minVersion: VersionString,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- runtime body only packages already-validated args; the strict contract (id → version → props) is enforced by FlowModuleHelper
  props: any,
  options?: { dataParams?: Record<string, unknown> },
): ModuleRef {
  return buildModuleRef(moduleId, minVersion, props, options);
}

export const flowModuleRef: FlowModuleHelper = createFlowModuleRef;

// ============================================================================
// loadingModuleRef() helper
// ============================================================================

/**
 * Type-safe reference helper for loading modules — `Flow.loading` and
 * `View.loadingOverride`.
 *
 * Accepts only ids registered with `moduleType: "loading"`; props are static
 * and checked against the {@link ModuleRegistry} contract for the id + version.
 */
export type LoadingModuleHelper = <
  TId extends LoadingModuleIdArg,
  TVersion extends LoadingModuleVersionArg<TId>,
>(
  moduleId: TId,
  minVersion: TVersion,
  props: RegisteredModuleProps<TId, TVersion>,
) => LoadingModuleRef;

// A prototype check rather than `typeof`: arrays, Dates, Maps and class
// instances are objects too, but fail the record schema or change shape in JSON.
export function isPropsRecord(
  value: unknown,
): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const prototype: unknown = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function createLoadingModuleRef(
  moduleId: ModuleID,
  minVersion: VersionString,
  // `unknown` for the same reason as createErrorModuleRef; call sites are
  // checked by LoadingModuleHelper.
  props: unknown,
): LoadingModuleRef {
  if (!isPropsRecord(props)) {
    throw new TypeError(
      `loadingModuleRef("${moduleId}") props must be a plain object.`,
    );
  }
  return { _tag: "LoadingModuleRef", moduleId, minVersion, props };
}

export const loadingModuleRef: LoadingModuleHelper = createLoadingModuleRef;
